import React, { Component } from 'react';
import { Switch, Route, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import { Spin } from 'antd';
import queryString from 'query-string';
import signUpRoutes from './routes/signUpRoutes';
import {
  getTrooprBotUser,
  getWorkspace,
  switchToRecentWorkspace,
  getProfileinfo,
  getUserWorkspaceMembership,
} from './components/common/common_action';
import { checkSlackLink } from './components/skills/skills_action.js';
import { fetchMe } from './components/auth/troopr/authApi';
import TrooprWorkspaces from './components/auth/troopr/TrooprWorkspaces';

// import {getAssisantSkills} from "./components/skills/skills_action"
import App from './App';
import JiraLogins from './JiraLogins';

// Full-screen authed flows that should NOT auto-resolve/enter a workspace.
const isWorkspaceSelectorPath = () =>
  window.location.pathname.startsWith('/user-workspaces') ||
  window.location.pathname.startsWith('/onboarding');

const isValidWorkspace = (id) => id && id.length === 24; //TODO:improve this validation

// When the resolved workspace is flagged isNewUi we bounce to the new app
// (mounted at /v2) — EXCEPT while an OAuth callback / billing / botupdate flow
// is mid-completion in the legacy app (those redirect to /v2 themselves once
// done). Guards a direct paste of a legacy URL for a new-UI workspace.
const isNewUiRedirectBlocked = () => {
  const path = window.location.pathname;
  if (path.startsWith('/v2') || path.startsWith('/troopr_billing')) return true;
  const qs = queryString.parse(window.location.search) || {};
  // OAuth returns / one-off flows land back on the legacy app first.
  return !!(
    qs.jira_login_type ||
    qs.jira_type ||
    qs.code ||
    qs.state ||
    qs.type ||
    qs.source
  );
};

class Routes extends Component {
  constructor(props) {
    super(props);
    // Legacy Bearer-token session (old flow). New flow uses httpOnly cookies.
    this.token = localStorage.getItem('token');
    this.state = {
      slackLinkStatus: null,
      loading: true,
      // authReady: have we finished deciding logged-in vs not? For token users
      // this is immediate; for cookie users we must first call /api/auth/me.
      authReady: !!this.token,
      cookieAuthed: false,
    };
  }

  async componentDidMount() {
    // On the workspace-selector screen we only confirm auth — we must NOT
    // auto-resolve/enter a workspace (the user picks one there).
    const onSelector = isWorkspaceSelectorPath();

    if (this.token) {
      if (!onSelector) await this.bootstrapWorkspace();
      this.setState({ authReady: true });
      return;
    }

    // No Bearer token — check for a cookie session (new Enjo-style auth).
    try {
      const res = await fetchMe();
      if (res && res.data && res.data.success) {
        if (res.data.user && res.data.user._id) {
          localStorage.setItem('trooprUserId', res.data.user._id);
        }
        this.setState({ cookieAuthed: true });
        if (!onSelector) await this.bootstrapWorkspace();
        this.setState({ authReady: true });
        return;
      }
    } catch (e) {
      // not signed in
    }
    this.setState({ authReady: true });
  }

  // Resolve the active workspace and hydrate the store. Works for both the
  // Bearer (header) and cookie sessions — API calls carry whichever applies.
  async bootstrapWorkspace() {
    // Workspace priority: 1) URL  2) localStorage  3) Slack teamId  4) recent
    let wId;
    const wId_url = window.location.href.split('/')[3];
    const wId_local = localStorage.getItem('userCurrentWorkspaceId');

    wId = isValidWorkspace(wId_url)
      ? wId_url
      : isValidWorkspace(wId_local)
      ? wId_local
      : null;

    if (!wId) {
      const teamId = localStorage.getItem('teamId');
      if (teamId) {
        try {
          const ws_team = await axios.get('/bot/slack/getTeamworkspace/' + teamId);
          if (
            ws_team.data.success &&
            ws_team.data.teamData &&
            ws_team.data.teamData.length > 0
          )
            wId = ws_team.data.teamData[0].workspace_id;
        } catch (e) {
          console.error('Error fetching workspace for Slack team', e);
        }
      }
    }

    if (wId) {
      if (wId_local !== wId) {
        axios.interceptors.request.use((config) => {
          config.headers['wId'] = wId;
          return config;
        });
        localStorage.setItem('userCurrentWorkspaceId', wId);
      }
      // Resolve the workspace first so we can honor its isNewUi flag: a new-UI
      // workspace opened via a legacy URL should bounce to /v2 automatically.
      const wsRes = await this.props.getWorkspace(wId);
      const ws = wsRes && wsRes.data && wsRes.data.workspace;
      if (ws && ws.isNewUi && !isNewUiRedirectBlocked()) {
        window.location.replace('/v2/' + wId);
        return;
      }
      this.props.getUserWorkspaceMembership(wId);
      this.props.getTrooprBotUser(wId);
      const slackLinkResponse = await this.props.checkSlackLink(wId);
      this.setState({
        slackLinkStatus:
          slackLinkResponse &&
          slackLinkResponse.data &&
          slackLinkResponse.data.team &&
          slackLinkResponse.data.team.status,
        loading: false,
      });
    } else {
      // Await so localStorage.userCurrentWorkspaceId (and the wId request
      // header it feeds) is set BEFORE the app renders and fires
      // workspace-scoped calls like /bot/api/w/:wId/skills — otherwise those
      // race ahead with no wId header and 403, bouncing fresh OAuth logins to
      // /user_unauthorized.
      await this.props.switchToRecentWorkspace();
      this.setState({ loading: false });
    }

    // Populate the logged-in user profile.
    this.props.getProfileinfo().then(() => {});
  }

  render() {
    const url = this.props.location.pathname;
    const { authReady, cookieAuthed } = this.state;
    const isAuthed = this.token || cookieAuthed;

    // While confirming a possible cookie session, avoid flashing the login UI.
    if (!authReady) {
      return (
        <div
          style={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin />
        </div>
      );
    }

    return (
      <div>
        <Switch>
          <Route
            path='/'
            render={() => {
              if (isAuthed && !url.match('/troopr_billing')) {
                // Enjo-style "Choose your workspace" screen after sign-in/up.
                if (url.startsWith('/user-workspaces'))
                  return <TrooprWorkspaces />;
                const qs = queryString.parse(window.location.search);
                if (qs.jira_login_type && qs.jira_type)
                  return (
                    <JiraLogins
                      queryString={qs}
                      workspace_id={window.location.href.split('/')[3]}
                    />
                  );
                else if (qs.type && qs.type === 'botupdate')
                  window.open(
                    'https://app.troopr.io/slack?source=botupdate',
                    '_self'
                  );
                // Authenticated users always reach the app. (Previously an
                // 'app_uninstalled' Slack state bounced them to the sign-in
                // page — wrong now that auth is email/OAuth based; the app
                // surfaces the reconnect-Slack prompt internally.)
                else return <App />;
              } else {
                return <Route component={signUpRoutes} />;
              }
            }}
          />
        </Switch>
      </div>
    );
  }
}

function mapStateToProps(store) {
  return {};
}

export default withRouter(
  connect(mapStateToProps, {
    getTrooprBotUser,
    getProfileinfo,
    getWorkspace,
    switchToRecentWorkspace /*getAssisantSkills*/,
    getUserWorkspaceMembership,
    checkSlackLink,
  })(Routes)
);
